import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Box,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    CircularProgress,
    Button,
    ButtonGroup
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    Close,
    CalendarToday,
    LocationOn,
    NavigateBefore,
    NavigateNext,
    Business,
    Construction,
    Schedule
} from '@mui/icons-material';
import { projectService } from '../services/api';
import { API_URL } from '../services/api';
import '../styles/Projects.scss';

const Projects = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await projectService.getAllProjects();
            setProjects(response.projects);
            setError(null);
        } catch (err) {
            console.error('Error fetching projects:', err);
            setError('Nem sikerült betölteni a projekteket. Kérjük, próbálja újra később.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return 'Függőben';
            case 'in_progress': return 'Folyamatban';
            case 'completed': return 'Befejezett';
            case 'cancelled': return 'Törölve';
            default: return status;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'in_progress': return 'primary';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const handleProjectClick = (project) => {
        setSelectedProject(project);
    };

    const handleImageClick = (image, index) => {
        setSelectedImage(image);
        setCurrentImageIndex(index);
    };

    const handleCloseImageViewer = () => {
        setSelectedImage(null);
    };

    const handlePrevImage = () => {
        if (!selectedProject) return;
        const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : selectedProject.images.length - 1;
        setCurrentImageIndex(newIndex);
        setSelectedImage(selectedProject.images[newIndex]);
    };

    const handleNextImage = () => {
        if (!selectedProject) return;
        const newIndex = currentImageIndex < selectedProject.images.length - 1 ? currentImageIndex + 1 : 0;
        setCurrentImageIndex(newIndex);
        setSelectedImage(selectedProject.images[newIndex]);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    const getProjectStats = () => {
        const totalProjects = projects.length;
        const ongoingCount = projects.filter(p => p.status === 'in_progress' || p.status === 'pending').length;
        const completedCount = projects.filter(p => p.status === 'completed').length;
        const totalArea = projects.reduce((sum, project) => sum + (project.area || 0), 0);

        return { totalProjects, ongoingCount, completedCount, totalArea };
    };

    const getFilteredProjects = () => {
        if (selectedStatus === 'all') return projects;
        if (selectedStatus === 'ongoing') {
            return projects.filter(p => p.status === 'in_progress' || p.status === 'pending');
        }
        return projects.filter(p => p.status === selectedStatus);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 70px)">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 70px)">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const stats = getProjectStats();
    const filteredProjects = getFilteredProjects();

    return (
        <div className="projects-page">
            <motion.div
                className="projects-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            >
                <Container>
                    <Typography variant="h1" className="page-title">
                        Referencia Projektjeink
                    </Typography>
                    <Typography variant="h2" className="page-subtitle">
                        Országszerte megvalósított építőipari beruházások
                    </Typography>
                    <div className="hero-stats">
                        <div className="stat-item">
                            <Business />
                            <div className="stat-content">
                                <span className="stat-value">{stats.totalProjects}</span>
                                <span className="stat-label">Összes projekt</span>
                            </div>
                        </div>
                        <div className="stat-item">
                            <Construction />
                            <div className="stat-content">
                                <span className="stat-value">{stats.ongoingCount}</span>
                                <span className="stat-label">Folyamatban</span>
                            </div>
                        </div>
                        <div className="stat-item">
                            <Schedule />
                            <div className="stat-content">
                                <span className="stat-value">{stats.completedCount}</span>
                                <span className="stat-label">Befejezett</span>
                            </div>
                        </div>
                        
                    </div>
                </Container>
            </motion.div>

            <Container className="projects-container">
                <Box className="status-filter" mb={4}>
                    <ButtonGroup variant="contained" color="primary">
                        <Button
                            onClick={() => setSelectedStatus('all')}
                            className={selectedStatus === 'all' ? 'active' : ''}
                        >
                            Összes projekt
                        </Button>
                        <Button
                            onClick={() => setSelectedStatus('ongoing')}
                            className={selectedStatus === 'ongoing' ? 'active' : ''}
                        >
                            Folyamatban
                        </Button>
                        <Button
                            onClick={() => setSelectedStatus('completed')}
                            className={selectedStatus === 'completed' ? 'active' : ''}
                        >
                            Befejezett
                        </Button>
                    </ButtonGroup>
                </Box>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <Grid container spacing={3}>
                        {filteredProjects.map((project, index) => (
                            <Grid item xs={12} sm={6} md={4} key={project._id}>
                                <motion.div variants={itemVariants}>
                                    <Card 
                                        className="project-card"
                                        onClick={() => handleProjectClick(project)}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="240"
                                            image={`${API_URL}/uploads/projects/${project.images[0]}`}
                                            alt={project.title}
                                            className="project-image"
                                        />
                                        <CardContent>
                                            <Typography variant="h5" className="project-title">
                                                {project.title}
                                            </Typography>
                                            <Box className="project-info">
                                                <Chip
                                                    label={getStatusLabel(project.status)}
                                                    color={getStatusColor(project.status)}
                                                    size="small"
                                                    className="status-chip"
                                                />
                                                <Typography variant="body2" className="location">
                                                    {project.location}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>

            {/* Project Details Dialog */}
            <Dialog
                open={Boolean(selectedProject)}
                onClose={() => setSelectedProject(null)}
                maxWidth="lg"
                fullWidth
                className="project-dialog"
            >
                {selectedProject && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <DialogContent className="dialog-content">
                            <IconButton
                                onClick={() => setSelectedProject(null)}
                                className="close-button"
                            >
                                <Close />
                            </IconButton>

                            <Grid container spacing={4}>
                                <Grid item xs={12} md={7}>
                                    <div className="project-gallery">
                                        <div
                                            className="main-image"
                                            onClick={() => handleImageClick(selectedProject.images[0], 0)}
                                            style={{ cursor: 'pointer', position: 'relative' }}
                                        >
                                            <img
                                                src={`${API_URL}/uploads/projects/${selectedProject.images[0]}`}
                                                alt={selectedProject.title}
                                                style={{
                                                    width: '100%',
                                                    height: '400px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <div className="image-overlay" style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'rgba(0,0,0,0.3)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    opacity: 1
                                                }
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                                                    Kattintson a nagyításhoz
                                                </Typography>
                                            </div>
                                        </div>
                                        {selectedProject.images && selectedProject.images.length > 1 && (
                                            <Box 
                                                sx={{ 
                                                    display: 'flex',
                                                    gap: 1,
                                                    mt: 2,
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                {selectedProject.images.map((image, index) => (
                                                    <Box
                                                        key={index}
                                                        onClick={() => handleImageClick(image, index)}
                                                        sx={{
                                                            width: 80,
                                                            height: 80,
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            cursor: 'pointer',
                                                            border: selectedImage === image ? '2px solid #2c5049' : 'none',
                                                            position: 'relative',
                                                            '&:hover': {
                                                                '&::after': {
                                                                    opacity: 0
                                                                }
                                                            },
                                                            '&::after': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: 'rgba(0,0,0,0.3)',
                                                                opacity: selectedImage === image ? 0 : 1,
                                                                transition: 'opacity 0.3s ease'
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={`${API_URL}/uploads/projects/${image}`}
                                                            alt={`${selectedProject.title} - ${index + 1}`}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </div>
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <div className="project-details">
                                        <Typography variant="h4" gutterBottom>
                                            {selectedProject.title}
                                        </Typography>

                                        <Chip
                                            label={getStatusLabel(selectedProject.status)}
                                            color={getStatusColor(selectedProject.status)}
                                            sx={{ mb: 2 }}
                                        />

                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <LocationOn sx={{ mr: 1 }} />
                                                <Typography>
                                                    <strong>Helyszín:</strong> {selectedProject.location}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <CalendarToday sx={{ mr: 1 }} />
                                                <Typography>
                                                    <strong>Kezdés:</strong>{' '}
                                                    {new Date(selectedProject.start_date).toLocaleDateString('hu')}
                                                    {selectedProject.end_date && (
                                                        <>
                                                            {' - '}
                                                            {new Date(selectedProject.end_date).toLocaleDateString('hu')}
                                                        </>
                                                    )}
                                                </Typography>
                                            </Box>

                                            
                                        </Box>

                                        {selectedProject.description && (
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    Projekt leírása
                                                </Typography>
                                                <Typography>
                                                    {selectedProject.description}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            href={`/kapcsolat?project=${encodeURIComponent(selectedProject.title)}`}
                                            sx={{ mt: 2 }}
                                        >
                                            Kapcsolatfelvétel
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </motion.div>
                )}
            </Dialog>

            {/* Image Viewer Dialog */}
            <Dialog
                open={Boolean(selectedImage)}
                onClose={handleCloseImageViewer}
                maxWidth="xl"
                fullWidth
                className="image-viewer"
            >
                <DialogContent sx={{ position: 'relative', p: 0, overflow: 'hidden', height: '90vh', background: 'rgba(0, 0, 0, 0.95)' }}>
                    <IconButton
                        onClick={handleCloseImageViewer}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.4)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.6)'
                            },
                            zIndex: 1
                        }}
                    >
                        <Close />
                    </IconButton>

                    {selectedProject && selectedProject.images && selectedProject.images.length > 1 && (
                        <>
                            <IconButton
                                onClick={handlePrevImage}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    zIndex: 2,
                                }}
                            >
                                <NavigateBefore />
                            </IconButton>
                            <IconButton
                                onClick={handleNextImage}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    zIndex: 2,
                                }}
                            >
                                <NavigateNext />
                            </IconButton>
                        </>
                    )}

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                        }}
                    >
                        <Box
                            component="img"
                            src={`${API_URL}/uploads/projects/${selectedImage}`}
                            alt="Nagyított kép"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                userSelect: 'none',
                                pointerEvents: 'none',
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </motion.div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Projects; 